import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  Typography,
  Alert,
  Select,
  Option,
  FormControl,
  FormLabel,
  Checkbox,
  Table,
  LinearProgress,
  Chip,
  Input,
  Stack,
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
} from '@mui/joy';
import { FileArrowUp as UploadFileIcon, Check as CheckIcon, Plus as AddIcon } from '@phosphor-icons/react';
import { insuranceApi, type InsuranceProvider } from '../../lib/insuranceApi';
import { useNotification } from '../../contexts/NotificationContext';

interface Props {
  onComplete?: () => void;
}

export default function InsurancePayoutUpload({ onComplete }: Props) {
  const { showSuccess, showError } = useNotification();
  const [providers, setProviders] = useState<InsuranceProvider[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<number | null>(null);
  const [createNewProvider, setCreateNewProvider] = useState(false);
  const [newProviderName, setNewProviderName] = useState('');
  const [newProviderType, setNewProviderType] = useState<'xactimate' | 'symbility' | 'simsol' | 'custom'>('xactimate');
  const [newProviderVersion, setNewProviderVersion] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<any>(null);
  const [showNewProviderModal, setShowNewProviderModal] = useState(false);

  // Load providers on mount
  useEffect(() => {
    loadProviders();
  }, []);

  const loadProviders = async () => {
    try {
      const response = await insuranceApi.providers.list({ is_active: true });
      const providerList = response?.results || [];
      setProviders(providerList);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to load insurance providers');
      setProviders([]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = [
        'text/csv',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ];
      
      if (!validTypes.includes(file.type) && !file.name.match(/\.(csv|xlsx?)$/i)) {
        setError('Please select a CSV or Excel file');
        return;
      }
      
      setSelectedFile(file);
      setError(null);
      setPreview(null);
    }
  };

  const handleCreateProvider = async () => {
    if (!newProviderName.trim()) {
      setError('Provider name is required');
      return;
    }

    try {
      const newProvider = await insuranceApi.providers.create({
        name: newProviderName,
        provider_type: newProviderType,
        version: newProviderVersion,
        effective_date: new Date().toISOString().split('T')[0],
        is_active: true,
      });

      setProviders([...providers, newProvider]);
      setSelectedProvider(newProvider.id);
      setShowNewProviderModal(false);
      setNewProviderName('');
      setNewProviderVersion('');
      showSuccess('Insurance provider created successfully');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create provider');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || (!selectedProvider && !createNewProvider)) {
      setError('Please select both a file and a provider');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await insuranceApi.payoutItems.uploadFile(
        selectedFile,
        createNewProvider ? undefined : selectedProvider || undefined,
        createNewProvider ? newProviderName : undefined,
        createNewProvider ? newProviderType : undefined,
        createNewProvider ? newProviderVersion : undefined,
        replaceExisting
      );

      if ((result as any).error) {
        setError((result as any).error);
      } else {
        setPreview(result);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to upload file');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!preview) return;

    setLoading(true);
    setError(null);

    try {
      const response = await insuranceApi.payoutItems.bulkImport(
        preview.provider.id,
        preview.all_items || preview.preview,
        replaceExisting
      );
      
      // Show success notification
      showSuccess(`Successfully imported ${response.created} items for ${preview.provider.name}`);
      
      // Reset form
      setPreview(null);
      setSelectedFile(null);
      setSelectedProvider(null);
      setReplaceExisting(false);
      setCreateNewProvider(false);
      setNewProviderName('');
      setNewProviderVersion('');
      
      // Reload providers in case we created a new one
      await loadProviders();
      
      // Notify parent component
      if (onComplete) {
        onComplete();
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || 'Failed to import items';
      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category: string): any => {
    const colors: Record<string, any> = {
      RFG: 'primary',
      PNT: 'warning',
      MISC: 'neutral',
    };
    return colors[category] || 'neutral';
  };

  const formatPrice = (price: any): string => {
    if (!price || price === 'N/A') return '-';
    return `$${parseFloat(price).toFixed(2)}`;
  };

  return (
    <>
      <Card>
        <Box sx={{ p: 3 }}>
          <Typography level="h3" sx={{ mb: 3 }}>
            Upload Insurance Payout Price List
          </Typography>

          {error && (
            <Alert color="danger" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!preview ? (
            <Box>
              <Stack spacing={2}>
                <FormControl>
                  <FormLabel>Insurance Provider</FormLabel>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Select
                      value={selectedProvider}
                      onChange={(_, value) => {
                        setSelectedProvider(value as number);
                        setCreateNewProvider(false);
                      }}
                      placeholder="Choose a provider"
                      disabled={createNewProvider}
                      sx={{ flex: 1 }}
                    >
                      {providers && providers.map((provider) => (
                        <Option key={provider.id} value={provider.id}>
                          {provider.name} {provider.version && `(v${provider.version})`}
                        </Option>
                      ))}
                    </Select>
                    <Button
                      variant="outlined"
                      color="primary"
                      startDecorator={<AddIcon />}
                      onClick={() => setShowNewProviderModal(true)}
                    >
                      New Provider
                    </Button>
                  </Box>
                </FormControl>

                <Box>
                  <Button
                    component="label"
                    startDecorator={<UploadFileIcon />}
                    variant="outlined"
                    color="primary"
                    fullWidth
                  >
                    {selectedFile ? selectedFile.name : 'Select Insurance Price List File (CSV or Excel)'}
                    <input
                      type="file"
                      hidden
                      accept=".csv,.xlsx,.xls"
                      onChange={handleFileSelect}
                    />
                  </Button>
                  <Typography level="body-sm" sx={{ mt: 1, color: 'text.secondary' }}>
                    Expected columns: CATEGORY, SELECTOR, DESCRIPTION, UNIT, REPLACE, REMOVE, DETACH & RESET
                  </Typography>
                </Box>

                <Checkbox
                  label="Replace existing price list for this provider"
                  checked={replaceExisting}
                  onChange={(e) => setReplaceExisting(e.target.checked)}
                />

                <Button
                  fullWidth
                  onClick={handleUpload}
                  disabled={!selectedFile || (!selectedProvider && !createNewProvider)}
                  loading={loading}
                  startDecorator={<UploadFileIcon />}
                >
                  Upload and Parse
                </Button>
              </Stack>
            </Box>
          ) : (
            <Box>
              <Alert color="success" sx={{ mb: 3 }}>
                Successfully parsed {preview.total_items} items from the file
              </Alert>

              <Box sx={{ mb: 2 }}>
                <Typography level="title-md">Provider: {preview.provider.name}</Typography>
                <Typography level="body-sm">Type: {preview.provider.provider_type}</Typography>
              </Box>

              <Typography level="title-md" sx={{ mb: 2 }}>
                Preview (First 10 Items)
              </Typography>

              <Box sx={{ overflowX: 'auto', mb: 3 }}>
                <Table size="sm">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Selector</th>
                      <th>Description</th>
                      <th>Unit</th>
                      <th>Replace</th>
                      <th>Remove</th>
                      <th>Detach & Reset</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview && preview.preview && preview.preview.map((item: any, index: number) => (
                      <tr key={index}>
                        <td>
                          <Chip size="sm" color={getCategoryColor(item.category)}>
                            {item.category}
                          </Chip>
                        </td>
                        <td>{item.selector_code}</td>
                        <td>{item.description}</td>
                        <td>{item.unit_of_measure || '-'}</td>
                        <td>{formatPrice(item.replace_price)}</td>
                        <td>{formatPrice(item.remove_price)}</td>
                        <td>{formatPrice(item.detach_reset_price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Box>

              {preview.total_items > 10 && (
                <Alert color="neutral" sx={{ mb: 3 }}>
                  ... and {preview.total_items - 10} more items
                </Alert>
              )}

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setPreview(null)}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmImport}
                  loading={loading}
                  startDecorator={<CheckIcon />}
                >
                  Import {preview.total_items} Items
                </Button>
              </Box>
            </Box>
          )}

          {loading && <LinearProgress sx={{ mt: 2 }} />}
        </Box>
      </Card>

      {/* New Provider Modal */}
      <Modal open={showNewProviderModal} onClose={() => setShowNewProviderModal(false)}>
        <ModalDialog>
          <ModalClose />
          <DialogTitle>Create New Insurance Provider</DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <FormControl>
                <FormLabel>Provider Name</FormLabel>
                <Input
                  value={newProviderName}
                  onChange={(e) => setNewProviderName(e.target.value)}
                  placeholder="e.g., Xactimate"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Provider Type</FormLabel>
                <Select
                  value={newProviderType}
                  onChange={(_, value) => setNewProviderType(value as any)}
                >
                  <Option value="xactimate">Xactimate</Option>
                  <Option value="symbility">Symbility</Option>
                  <Option value="simsol">Simsol</Option>
                  <Option value="custom">Custom</Option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Version (Optional)</FormLabel>
                <Input
                  value={newProviderVersion}
                  onChange={(e) => setNewProviderVersion(e.target.value)}
                  placeholder="e.g., 2024.01"
                />
              </FormControl>

              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
                <Button variant="outlined" onClick={() => setShowNewProviderModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateProvider} disabled={!newProviderName.trim()}>
                  Create Provider
                </Button>
              </Box>
            </Stack>
          </DialogContent>
        </ModalDialog>
      </Modal>
    </>
  );
}